<script>
import QRCode from 'qrcode'

const DEFAULT_COUNTDOWN_SECONDS = 0

function safeHttpsUrl (value) {
  try {
    const url = new URL(String(value || '').trim())
    return url.protocol === 'https:' ? url.href : ''
  } catch (error) {
    return ''
  }
}

function safeQrImage (value) {
  const text = String(value || '').trim()
  if (!text) return ''
  if (/^https:\/\//i.test(text)) return safeHttpsUrl(text)
  if (/^data:image\/(png|jpeg|jpg|webp);base64,[A-Za-z0-9+/=\r\n]+$/i.test(text)) return text
  if (/^[A-Za-z0-9+/=\r\n]+$/.test(text)) return `data:image/png;base64,${text.replace(/\s+/g, '')}`
  return ''
}

function qrPayloadImage (value) {
  const text = String(value || '').trim()
  if (!text) return ''
  if (/^data:image\/(png|jpeg|jpg|webp);base64,[A-Za-z0-9+/=\r\n]+$/i.test(text)) return text
  if (/^(iVBORw0KGgo|\/9j\/|UklGR)[A-Za-z0-9+/=\r\n]+$/.test(text)) {
    const mimeType = text.startsWith('/9j/') ? 'jpeg' : (text.startsWith('UklGR') ? 'webp' : 'png')
    return `data:image/${mimeType};base64,${text.replace(/\s+/g, '')}`
  }
  return ''
}

function timestampMs (value) {
  if (!value) return 0
  if (value instanceof Date) return value.getTime()
  const numeric = Number(value)
  if (Number.isFinite(numeric) && numeric > 0) return numeric < 100000000000 ? numeric * 1000 : numeric
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : 0
}

export default {
  name: 'UnifiedDepositPaymentPanel',
  props: {
    customerAccount: { type: Object, default: () => ({}) },
    receiverAccount: { type: Object, default: () => ({}) },
    qrImage: { type: String, default: '' },
    qrPayload: { type: String, default: '' },
    paymentUrl: { type: String, default: '' },
    amount: { type: [Number, String], default: '' },
    amountPending: { type: Boolean, default: false },
    remainingText: { type: String, default: '' },
    expiresAt: { type: [Number, String, Date], default: null },
    fallbackCountdownSeconds: { type: Number, default: DEFAULT_COUNTDOWN_SECONDS },
    checking: { type: Boolean, default: false },
    cancelling: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    showCancel: { type: Boolean, default: true },
    showUpload: { type: Boolean, default: false }
  },
  data () {
    const nowMs = Date.now()
    const fallbackSeconds = Math.max(0, Number(this.fallbackCountdownSeconds || 0))
    return {
      nowMs,
      fallbackExpiresAtMs: fallbackSeconds ? nowMs + fallbackSeconds * 1000 : 0,
      countdownTimer: null,
      iframeLoaded: false,
      generatedQrImage: '',
      qrPayloadLoading: false,
      qrGenerationVersion: 0,
      qrDownloading: false
    }
  },
  computed: {
    safeQrImageUrl () {
      return safeQrImage(this.qrImage) || this.generatedQrImage
    },
    normalizedQrPayload () {
      return String(this.qrPayload || '').trim()
    },
    safePaymentUrl () {
      return safeHttpsUrl(this.paymentUrl)
    },
    hasReceiverAccount () {
      return Boolean(String(this.receiverAccount.accountNo || '').trim())
    },
    displayType () {
      if (this.safeQrImageUrl) return 'qr'
      if (this.normalizedQrPayload && this.qrPayloadLoading) return 'qr-loading'
      if (this.hasReceiverAccount) return 'account'
      if (this.safePaymentUrl) return 'frame'
      return 'unavailable'
    },
    formattedAmount () {
      const text = String(this.amount === null || this.amount === undefined ? '' : this.amount).trim()
      if (!text) return ''
      const numeric = Number(text.replace(/,/g, ''))
      if (!Number.isFinite(numeric)) return text
      const hasDecimal = /\.\d+/.test(text)
      return numeric.toLocaleString('th-TH', {
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: 2
      })
    },
    resolvedExpiresAtMs () {
      return timestampMs(this.expiresAt) || this.fallbackExpiresAtMs
    },
    calculatedRemainingText () {
      if (!this.resolvedExpiresAtMs) return ''
      const seconds = Math.max(0, Math.ceil((this.resolvedExpiresAtMs - this.nowMs) / 1000))
      const minutes = Math.floor(seconds / 60)
      return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
    },
    displayedRemainingText () {
      return this.remainingText || this.calculatedRemainingText
    },
    customerName () {
      return String(this.customerAccount.accountName || '').trim() || '-'
    },
    customerBankCode () {
      const explicitCode = String(this.customerAccount.bankCode || '').trim()
      const bankNamePrefix = String(this.customerAccount.bankName || '').split(/\s*[-–]\s*/)[0].trim()
      return (explicitCode || bankNamePrefix || '-').toUpperCase()
    },
    customerNumber () {
      return String(this.customerAccount.accountNo || '').trim() || '-'
    }
  },
  watch: {
    qrPayload: {
      immediate: true,
      handler (value) {
        this.generateQrPayload(value)
      }
    },
    safePaymentUrl () {
      this.iframeLoaded = false
    }
  },
  mounted () {
    this.countdownTimer = window.setInterval(() => {
      this.nowMs = Date.now()
    }, 1000)
  },
  beforeDestroy () {
    this.qrGenerationVersion += 1
    if (this.countdownTimer) window.clearInterval(this.countdownTimer)
  },
  methods: {
    async generateQrPayload (value) {
      const payload = String(value || '').trim()
      const generationVersion = ++this.qrGenerationVersion
      this.generatedQrImage = ''
      this.qrPayloadLoading = Boolean(payload)
      if (!payload) return

      const imageValue = qrPayloadImage(payload)
      if (imageValue) {
        this.generatedQrImage = imageValue
        this.qrPayloadLoading = false
        return
      }

      try {
        const generatedImage = await QRCode.toDataURL(payload, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 360,
          type: 'image/png',
          color: { dark: '#000000', light: '#ffffff' }
        })
        if (generationVersion === this.qrGenerationVersion) this.generatedQrImage = generatedImage
      } catch (error) {
        console.warn('Unable to generate deposit QR Code:', error && error.message ? error.message : error)
      } finally {
        if (generationVersion === this.qrGenerationVersion) this.qrPayloadLoading = false
      }
    },
    handleFrameLoad () {
      this.iframeLoaded = true
    },
    async downloadQrCode () {
      const source = this.safeQrImageUrl
      if (!source || this.qrDownloading) return

      this.qrDownloading = true
      try {
        const response = await fetch(source, { credentials: 'omit', cache: 'no-store' })
        if (!response.ok) throw new Error(`QR image request failed with status ${response.status}`)
        const blob = await response.blob()
        if (!/^image\/(png|jpe?g|webp)$/i.test(blob.type)) throw new Error('QR image has an unsupported file type')

        const extension = /jpe?g/i.test(blob.type) ? 'jpg' : (/webp/i.test(blob.type) ? 'webp' : 'png')
        const downloadUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `qrcode-${Date.now()}.${extension}`
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000)
      } catch (error) {
        const link = document.createElement('a')
        link.href = source
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        link.remove()
        if (this.$swal) {
          this.$swal({
            icon: 'info',
            title: 'เปิดรูปคิวอาร์โค้ดแล้ว',
            text: 'หากเบราว์เซอร์ไม่บันทึกอัตโนมัติ กรุณากดค้างที่รูปแล้วเลือกบันทึกรูปภาพ'
          })
        }
      } finally {
        this.qrDownloading = false
      }
    },
    async copyReceiverAccount () {
      const accountNo = String(this.receiverAccount.accountNo || '').replace(/[^0-9A-Za-z]/g, '')
      if (!accountNo) return
      try {
        await navigator.clipboard.writeText(accountNo)
        if (this.$swal) {
          this.$swal({ icon: 'success', title: 'คัดลอกเลขบัญชีแล้ว', timer: 1000, showConfirmButton: false })
        }
      } catch (error) {
        if (this.$swal) this.$swal({ icon: 'error', title: 'คัดลอกไม่สำเร็จ', text: 'กรุณากดค้างที่เลขบัญชีเพื่อคัดลอก' })
      }
    },
    openUploadPicker () {
      if (this.uploading || this.cancelling || !this.$refs.uploadInput) return
      this.$refs.uploadInput.click()
    }
  }
}
</script>

<template>
  <section class="deposit-payment" aria-live="polite">
    <header class="deposit-payment__customer">
      <button
        type="button"
        class="deposit-payment__top-close"
        data-dismiss="modal"
        :disabled="cancelling || uploading"
        aria-label="ปิดหน้าต่าง"
      >
        <i class="fas fa-times" aria-hidden="true" />
      </button>
      <div class="deposit-payment__eyebrow">
        โปรดโอนจากบัญชีนี้เท่านั้น
      </div>
      <div
        class="deposit-payment__customer-card"
        :class="{ 'deposit-payment__customer-card--without-logo': !customerAccount.bankImage }"
      >
        <img
          v-if="customerAccount.bankImage"
          class="deposit-payment__bank-logo"
          :src="customerAccount.bankImage"
          alt="ธนาคารของบัญชีผู้โอน"
        >
        <div class="deposit-payment__customer-detail">
          <div class="deposit-payment__customer-account-row">
            <strong class="deposit-payment__customer-bank-code">{{ customerBankCode }}</strong>
            <span class="deposit-payment__customer-number">{{ customerNumber }}</span>
          </div>
          <span class="deposit-payment__customer-name">{{ customerName }}</span>
        </div>
      </div>
    </header>

    <main class="deposit-payment__content">
      <div v-if="formattedAmount || amountPending || displayedRemainingText" class="deposit-payment__summary">
        <div v-if="formattedAmount || amountPending" class="deposit-payment__amount">
          <span>ยอดที่ต้องโอน</span>
          <strong v-if="formattedAmount">{{ formattedAmount }} <small>บาท</small></strong>
          <span v-else role="status">กำลังตรวจสอบยอดโอน</span>
        </div>
        <div v-if="displayedRemainingText" class="deposit-payment__timer" role="timer" aria-atomic="true">
          <span>เวลาคงเหลือ</span>
          <strong>{{ displayedRemainingText }}</strong>
        </div>
      </div>

      <div v-if="displayType === 'qr'" class="deposit-payment__qr-area">
        <div class="deposit-payment__qr-frame">
          <img class="deposit-payment__qr" :src="safeQrImageUrl" alt="QR Code สำหรับชำระเงิน">
        </div>
        <button
          type="button"
          class="deposit-payment__qr-download"
          value="บันทึก คิวอาร์โค้ด"
          :disabled="qrDownloading || cancelling || uploading"
          @click="downloadQrCode"
        >
          <i :class="qrDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'" aria-hidden="true" />
          {{ qrDownloading ? 'กำลังบันทึก...' : 'บันทึก คิวอาร์โค้ด' }}
        </button>
      </div>

      <div v-else-if="displayType === 'qr-loading'" class="deposit-payment__qr-loading" role="status">
        <i class="fas fa-spinner fa-spin" aria-hidden="true" />
        <span>กำลังสร้าง QR Code</span>
      </div>

      <div v-else-if="displayType === 'account'" class="deposit-payment__receiver">
        <div class="deposit-payment__receiver-card">
          <img
            v-if="receiverAccount.bankImage"
            class="deposit-payment__receiver-logo"
            :src="receiverAccount.bankImage"
            alt="ธนาคารผู้รับ"
          >
          <div class="deposit-payment__receiver-detail">
            <span class="deposit-payment__receiver-bank">{{ receiverAccount.bankName || 'บัญชีรับเงิน' }}</span>
            <div class="deposit-payment__receiver-number">
              {{ receiverAccount.accountNo }}
            </div>
            <strong class="deposit-payment__receiver-name">{{ receiverAccount.accountName || '-' }}</strong>
          </div>
          <button type="button" class="deposit-payment__copy" aria-label="คัดลอกเลขบัญชี" @click="copyReceiverAccount">
            <i class="far fa-copy" aria-hidden="true" />
            คัดลอก
          </button>
        </div>
      </div>

      <div v-else-if="displayType === 'frame'" class="deposit-payment__frame-area">
        <div v-if="!iframeLoaded" class="deposit-payment__frame-loading" role="status">
          <i class="fas fa-spinner fa-spin" aria-hidden="true" />
          <span>กำลังโหลดข้อมูลชำระเงิน</span>
        </div>
        <iframe
          :key="safePaymentUrl"
          class="deposit-payment__iframe"
          :src="safePaymentUrl"
          title="หน้าชำระเงิน"
          loading="eager"
          referrerpolicy="strict-origin-when-cross-origin"
          allow="camera; fullscreen; clipboard-read; clipboard-write; payment"
          @load="handleFrameLoad"
        />
      </div>

      <div v-else class="deposit-payment__unavailable" role="alert">
        ไม่พบ QR Code หรือข้อมูลบัญชี กรุณาปิดหน้าต่างแล้วลองใหม่
      </div>

      <div v-if="showUpload" class="deposit-payment__upload-section">
        <input
          ref="uploadInput"
          class="deposit-payment__file-input"
          type="file"
          accept="image/*"
          :disabled="uploading || cancelling"
          @change="$emit('upload', $event)"
        >
        <button
          type="button"
          class="deposit-payment__upload"
          :disabled="uploading || cancelling"
          @click="openUploadPicker"
        >
          <i :class="uploading ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-upload-alt'" aria-hidden="true" />
          {{ uploading ? 'กำลังอัปโหลดสลิป...' : 'อัปโหลดสลิป' }}
        </button>
      </div>

      <div class="deposit-payment__warnings">
        <div class="deposit-payment__warning deposit-payment__warning--ok">
          <span aria-hidden="true">✅</span>
          <strong>โอนตรงตามยอดเงินที่ระบุ</strong>
        </div>
        <div class="deposit-payment__warning deposit-payment__warning--ok">
          <span aria-hidden="true">✅</span>
          <strong>โอนจากบัญชี และเวลาที่กำหนดเท่านั้น</strong>
        </div>
        <div class="deposit-payment__warning deposit-payment__warning--danger">
          <span aria-hidden="true">⚠️</span>
          <strong>ห้ามใช้บัญชีหรือ QR Code เดิมโอนซ้ำ</strong>
        </div>
      </div>
    </main>

    <footer class="deposit-payment__actions">
      <button
        v-if="showCancel"
        type="button"
        class="deposit-payment__cancel"
        :disabled="checking || cancelling || uploading"
        @click="$emit('cancel')"
      >
        <i v-if="cancelling" class="fas fa-spinner fa-spin" aria-hidden="true" />
        {{ cancelling ? 'กำลังยกเลิกรายการ...' : 'ยกเลิกรายการเดิมและสร้างรายการใหม่' }}
      </button>
      <button type="button" class="deposit-payment__close" data-dismiss="modal" :disabled="cancelling || uploading">
        ปิดหน้าต่าง
      </button>
    </footer>
  </section>
</template>

<style scoped>
.deposit-payment {
  --ink: #34220c;
  --muted: #806b43;
  --paper: #fffaf0;
  --surface: #fffefa;
  --line: #ead49f;
  --gold-start: #b47e11;
  --gold-mid: #fec33f;
  --gold-end: #e09c14;
  --green: #337158;
  --green-dark: #76500d;
  --amber: #92510a;
  display: flex;
  flex-direction: column;
  width: min(calc(100% - 30px), 620px);
  max-height: min(720px, calc(100vh - 30px));
  min-height: 0;
  margin: 15px auto;
  overflow: hidden;
  border: 1px solid #ffd773;
  border-radius: 22px;
  background: var(--paper);
  color: var(--ink);
  font-family: 'Kanit', sans-serif;
  box-shadow: 0 20px 48px rgba(69, 40, 4, .32);
}
.deposit-payment__customer {
  position: relative;
  flex: 0 0 auto;
  min-height: 76px;
  padding: 10px 48px 9px 14px;
  overflow: hidden;
  background: linear-gradient(130deg, #8b5603, #e3a51e);
  color: #fff;
}
.deposit-payment__customer::after {
  position: absolute;
  top: -65px;
  right: -35px;
  width: 150px;
  height: 150px;
  border: 24px solid rgba(255, 255, 255, .08);
  border-radius: 50%;
  content: '';
}
.deposit-payment__top-close {
  position: absolute;
  top: 11px;
  right: 11px;
  z-index: 3;
  display: grid;
  width: 34px;
  height: 34px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, .18);
  color: #fff;
  font-size: 18px;
}
.deposit-payment__top-close:disabled { cursor: wait; opacity: .6; }
.deposit-payment__eyebrow {
  position: relative;
  z-index: 1;
  margin-bottom: 6px;
  color: #fff0c5;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .04em;
}
.deposit-payment__customer-card {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: 11px;
}
.deposit-payment__customer-card--without-logo { grid-template-columns: minmax(0, 1fr); }
.deposit-payment__bank-logo {
  width: 36px;
  height: 36px;
  padding: 3px;
  border-radius: 50%;
  background: #fff;
  object-fit: contain;
}
.deposit-payment__customer-detail {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}
.deposit-payment__customer-account-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
  white-space: nowrap;
}
.deposit-payment__customer-bank-code {
  flex: 0 0 auto;
  font-size: 15px;
  letter-spacing: .02em;
}
.deposit-payment__customer-number {
  overflow: hidden;
  min-width: 0;
  color: #fff;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: .04em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.deposit-payment__customer-name {
  overflow: hidden;
  color: rgba(255, 255, 255, .78);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.deposit-payment__content {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding: 11px;
  background: linear-gradient(180deg, #fffdf7, #fff7e8);
}
.deposit-payment__summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  gap: 10px;
}
.deposit-payment__amount,
.deposit-payment__timer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--surface);
  min-height: 56px;
}
.deposit-payment__amount { padding: 8px 12px; }
.deposit-payment__amount span,
.deposit-payment__timer span { color: var(--muted); font-size: 11px; }
.deposit-payment__amount strong { color: var(--green-dark); font-size: 22px; line-height: 1.1; }
.deposit-payment__amount small { font-size: 11px; }
.deposit-payment__timer { min-width: 104px; padding: 8px 12px; text-align: center; }
.deposit-payment__timer strong { color: #c7352a; font-size: 19px; font-variant-numeric: tabular-nums; }
.deposit-payment__section-label {
  color: var(--ink);
  font-size: 14px;
  font-weight: 800;
  text-align: center;
}
.deposit-payment__qr-area,
.deposit-payment__receiver {
  display: flex;
  flex: 1 0 auto;
  min-height: auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.deposit-payment__qr-area { flex: 0 0 auto; }
.deposit-payment__qr-frame {
  flex: 0 0 auto;
  width: min(218px, 68vw);
  padding: 8px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--surface);
  box-shadow: 0 12px 28px rgba(98, 60, 6, .13);
}
.deposit-payment__qr { display: block; width: 100%; height: auto; }
.deposit-payment__qr-download {
  width: min(218px, 68vw);
  min-height: 36px;
  padding: 7px 12px;
  border: 0;
  border-radius: 9px;
  background: linear-gradient(160deg, var(--gold-start), var(--gold-mid) 58%, var(--gold-end));
  color: #2e1b00;
  font-size: 12px;
  font-weight: 900;
}
.deposit-payment__qr-download i { margin-right: 6px; }
.deposit-payment__qr-download:disabled { cursor: wait; opacity: .6; }
.deposit-payment__receiver-card {
  display: grid;
  width: min(218px, 68vw);
  min-height: 218px;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto auto;
  align-items: center;
  justify-items: center;
  gap: 8px;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--surface);
  box-shadow: 0 12px 28px rgba(98, 60, 6, .12);
  text-align: center;
}
.deposit-payment__receiver-logo {
  width: 48px;
  height: 48px;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 50%;
  object-fit: contain;
}
.deposit-payment__receiver-detail {
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex-direction: column;
  align-items: center;
}
.deposit-payment__receiver-bank {
  overflow: hidden;
  max-width: 100%;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.deposit-payment__receiver-name {
  overflow: hidden;
  max-width: 100%;
  margin-top: 3px;
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.deposit-payment__receiver-number {
  margin-top: 3px;
  color: var(--green-dark);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: clamp(21px, 6vw, 25px);
  font-weight: 900;
  letter-spacing: .045em;
  overflow-wrap: anywhere;
}
.deposit-payment__copy {
  width: 100%;
  min-height: 34px;
  padding: 7px 10px;
  border: 0;
  border-radius: 9px;
  background: linear-gradient(160deg, var(--gold-start), var(--gold-mid) 58%, var(--gold-end));
  color: #2e1b00;
  font-size: 12px;
  font-weight: 800;
}
.deposit-payment__copy i { margin-right: 5px; }
.deposit-payment__frame-area {
  position: relative;
  flex: 1 1 auto;
  min-height: 360px;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 13px;
  background: var(--surface);
}
.deposit-payment__iframe { position: relative; z-index: 1; display: block; width: 100%; height: 100%; min-height: 460px; border: 0; background: #fff; }
.deposit-payment__frame-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--muted);
}
.deposit-payment__frame-loading i { color: var(--green); font-size: 28px; }
.deposit-payment__qr-loading {
  display: flex;
  flex: 1 1 auto;
  min-height: 220px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--muted);
}
.deposit-payment__qr-loading i { color: var(--green); font-size: 28px; }
.deposit-payment__unavailable {
  padding: 28px 18px;
  border: 1px solid #f3b9b3;
  border-radius: 12px;
  background: #fff5f4;
  color: #9f2f27;
  text-align: center;
}
.deposit-payment__upload-section {
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
}
.deposit-payment__warnings {
  display: grid;
  flex: 0 0 auto;
  gap: 6px;
}
.deposit-payment__warning {
  display: grid;
  min-height: 35px;
  grid-template-columns: 20px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  padding: 6px 9px;
  border-radius: 9px;
  font-size: 11px;
  line-height: 1.25;
}
.deposit-payment__warning--ok { border: 1px solid #b6d7c7; background: #f3f8f0; color: #285f4c; }
.deposit-payment__warning--danger { border: 1px solid #e9c985; background: #fff4dc; color: var(--amber); }
.deposit-payment__actions {
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(0, 1fr);
  flex: 0 0 auto;
  gap: 7px;
  padding: 9px 11px max(9px, env(safe-area-inset-bottom));
  border-top: 1px solid #d6a437;
  background: #4a2d05;
}
.deposit-payment__file-input { display: none; }
.deposit-payment__upload,
.deposit-payment__cancel,
.deposit-payment__close {
  width: 100%;
  min-height: 39px;
  padding: 8px 10px;
  border: 0;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 900;
}
.deposit-payment__actions > :only-child { grid-column: 1 / -1; }
.deposit-payment__upload {
  max-width: 560px;
  background: linear-gradient(160deg, var(--gold-start), var(--gold-mid) 58%, var(--gold-end));
  color: #2e1b00;
}
.deposit-payment__cancel { background: #fff0e9; color: #a73e30; }
.deposit-payment__close {
  background: linear-gradient(160deg, #e0a21c, #ffd773);
  color: #2e1b00;
}
.deposit-payment__upload:disabled,
.deposit-payment__cancel:disabled,
.deposit-payment__close:disabled { cursor: wait; opacity: .6; }
@media (max-width: 991.98px) {
  .deposit-payment {
    width: min(calc(100vw - 16px), 390px);
    max-height: min(720px, calc(100vh - 16px));
    max-height: min(720px, calc(100dvh - 16px));
    min-height: 0;
    margin: 8px auto;
  }
}
@media (max-width: 575.98px) {
  .deposit-payment__customer { padding: max(10px, env(safe-area-inset-top)) 42px 9px 13px; }
  .deposit-payment__customer-card { grid-template-columns: 36px minmax(0, 1fr); gap: 9px; }
  .deposit-payment__customer-card--without-logo { grid-template-columns: minmax(0, 1fr); }
  .deposit-payment__bank-logo { width: 36px; height: 36px; }
  .deposit-payment__content { gap: 10px; padding: 11px; }
  .deposit-payment__qr-frame { width: min(218px, 68vw); padding: 8px; border-radius: 16px; }
  .deposit-payment__receiver-card { width: min(218px, 68vw); min-height: 218px; gap: 7px; padding: 13px; }
  .deposit-payment__receiver-logo { width: 46px; height: 46px; }
  .deposit-payment__frame-area { min-height: 330px; }
  .deposit-payment__iframe { min-height: 400px; }
  .deposit-payment__warning { padding: 6px 9px; font-size: 11px; }
}
@media (max-width: 370px) {
  .deposit-payment { width: min(calc(100vw - 10px), 390px); margin: 5px auto; }
  .deposit-payment__content { padding-right: 8px; padding-left: 8px; }
  .deposit-payment__qr-frame { width: min(205px, 66vw); }
  .deposit-payment__receiver-card { width: min(205px, 66vw); min-height: 205px; }
}
</style>
